'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { GenerationType } from '@/hooks/useGeneration';
import { Bubble, Cpu, Driver, TaskSquare } from 'iconsax-reactjs';
import { SUPPORTED_PLATFORMS } from '@/lib/constants/platforms';

interface OutputSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOutput: (
    types: GenerationType[],
    documentNames?: Record<GenerationType, string>,
    technologyContext?: string,
    additionalPrompt?: string,
  ) => void;
  title?: string;
}

export function OutputSelectionModal({
  isOpen,
  onClose,
  onSelectOutput,
  title = 'Select Output Types',
}: OutputSelectionModalProps) {
  const [selectedType, setSelectedType] = useState<GenerationType | null>(null);
  const [documentName, setDocumentName] = useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [customPlatform, setCustomPlatform] = useState<string>('');
  const [additionalPrompt, setAdditionalPrompt] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const getPlaceholder = (type: GenerationType) => {
    return `${type} - V1 - Date`;
  };

  const outputTypes = [
    {
      type: 'Process Flow Doc' as GenerationType,
      icon: TaskSquare,
      description: 'Generate a process flow document',
    },
    {
      type: 'Discovery Doc' as GenerationType,
      icon: Bubble,
      description: 'Generate a discovery document',
    },
    {
      type: 'Backlog Doc' as GenerationType,
      icon: Driver,
      description: 'Generate a backlog document',
    },
    {
      type: 'Platform Backlog Doc' as GenerationType,
      icon: Cpu,
      description: 'Generate a technology-specific backlog with platform context',
    },
  ];

  // The effective platform is either the dropdown selection or the custom typed value
  const effectivePlatform = selectedPlatform === '__custom__' ? customPlatform.trim() : selectedPlatform;

  const handleTypeToggle = (type: GenerationType) => {
    if (selectedType === type) {
      setSelectedType(null);
      setDocumentName('');
      setSelectedPlatform('');
      setCustomPlatform('');
      setAdditionalPrompt('');
    } else {
      setSelectedType(type);
      setDocumentName('');
      setSelectedPlatform('');
      setCustomPlatform('');
      setAdditionalPrompt('');
    }
  };

  // Focus input when a type is selected (non-platform types)
  useEffect(() => {
    if (selectedType && selectedType !== 'Platform Backlog Doc' && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [selectedType]);

  // Focus file name input after a predefined platform is selected from dropdown
  // (not on custom platform keystrokes — that would steal focus)
  useEffect(() => {
    if (selectedPlatform && selectedPlatform !== '__custom__' && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [selectedPlatform]);

  const handleDocumentNameChange = (value: string) => {
    setDocumentName(value);
  };

  const isGenerateEnabled = () => {
    if (!selectedType) return false;
    if (selectedType === 'Platform Backlog Doc' && !effectivePlatform) return false;
    return documentName.trim() !== '';
  };

  const handleGenerate = () => {
    if (isGenerateEnabled() && selectedType) {
      const documentNames: Record<GenerationType, string> = {
        'Process Flow Doc': '',
        'Discovery Doc': '',
        'Backlog Doc': '',
        'Platform Backlog Doc': '',
        [selectedType]: documentName,
      };
      const techContext = selectedType === 'Platform Backlog Doc' ? effectivePlatform : undefined;
      const prompt = additionalPrompt.trim() || undefined;
      onSelectOutput([selectedType], documentNames, techContext, prompt);
      setSelectedType(null);
      setDocumentName('');
      setSelectedPlatform('');
      setCustomPlatform('');
      setAdditionalPrompt('');
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setDocumentName('');
    setSelectedPlatform('');
    setCustomPlatform('');
    setAdditionalPrompt('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-400 pt-2">
          Choose an output and enter a file name in the input field to generate.
        </p>
        <div className="py-4 space-y-3">
          {outputTypes.map(({ type, icon: Icon, description }) => (
            <div key={type}>
              <div
                className={`w-full border rounded-md transition-colors ${
                  selectedType === type
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <button
                  onClick={() => handleTypeToggle(type)}
                  className="w-full flex items-center space-x-3 p-3 text-left hover:bg-inherit"
                >
                  <Icon className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{type}</div>
                    <div className="text-sm text-gray-500">{description}</div>
                  </div>
                </button>
                {selectedType === type && (
                  <div className="pl-11 pr-3 pb-3 space-y-2">
                    {type === 'Platform Backlog Doc' && (
                      <>
                        <select
                          value={selectedPlatform}
                          onChange={(e) => {
                            setSelectedPlatform(e.target.value);
                            if (e.target.value !== '__custom__') {
                              setCustomPlatform('');
                            }
                          }}
                          className="w-full text-sm bg-white border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">Select a technology platform...</option>
                          {SUPPORTED_PLATFORMS.map((platform) => (
                            <option key={platform} value={platform}>
                              {platform}
                            </option>
                          ))}
                          <option value="__custom__">Other (type your own)...</option>
                        </select>
                        {selectedPlatform === '__custom__' && (
                          <Input
                            placeholder="e.g. Workday, HubSpot, Zoho CRM..."
                            value={customPlatform}
                            onChange={(e) => setCustomPlatform(e.target.value)}
                            className="w-full text-sm bg-white border-gray-300"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </>
                    )}
                    {(type !== 'Platform Backlog Doc' || effectivePlatform) && (
                      <>
                        <Input
                          ref={inputRef}
                          placeholder={getPlaceholder(type)}
                          value={documentName}
                          onChange={(e) =>
                            handleDocumentNameChange(e.target.value)
                          }
                          className="w-full text-sm bg-white border-gray-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                        {type === 'Platform Backlog Doc' && (
                          <textarea
                            placeholder="Additional context (optional) — Add any requirements, notes, or details not covered in the meeting that should be included in the backlog..."
                            value={additionalPrompt}
                            onChange={(e) => setAdditionalPrompt(e.target.value)}
                            className="w-full text-sm bg-white border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            rows={3}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex-row justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={handleClose} className="px-4 py-2">
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!isGenerateEnabled()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Generate {selectedType ? '(1)' : '(0)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
